/* eslint-disable @typescript-eslint/no-unused-vars */
import { TDestinationCredentials } from '@/core/apis/config_api/config_api_types';
import { MARKET_FILTERING_ENABLED } from '@/core/constants/appConfig/types';
import { TMableEvent } from '@/core/constants/event_types/base_mable_event';
import { EMableEventName } from '@/core/constants/event_types/events';
import { BrowserInstanceData } from '@/core/data_handlers/browser_instance';
import { MarketDataHandler } from '@/core/data_handlers/market';
import { EDestinationType } from '@/core/interfaces';

export type DestinationPayload = Record<string, any>;

export type DestinationSendingResult = {
  destinationReqEvent: any;
  reason?: string; // TODO: standardise reasons per EDestinationType
  success: boolean;
};

export type IDestinationBundle = {
  allowedEvents: EMableEventName[];
  calculatePayload: (
    mableEvent: TMableEvent,
    // shouldSend: boolean,
    // consent: any,
  ) => DestinationPayload;
  destinationConfig: any;
  init: (bid: BrowserInstanceData) => void;
  sendToDestination: (
    payload: DestinationPayload,
  ) => Promise<DestinationSendingResult>;
  type: EDestinationType;
};
export abstract class ADestinationBundle implements IDestinationBundle {
  abstract destinationId: string;
  abstract allowedEvents: EMableEventName[];
  abstract type: EDestinationType;
  abstract destinationConfig: any;
  abstract init(bid: BrowserInstanceData): void;
  abstract calculatePayload(
    mableEvent: TMableEvent, // shouldSend: boolean,
  ): DestinationPayload;
  abstract sendToDestination(
    payload: DestinationPayload,
  ): Promise<DestinationSendingResult>;
}

// Example implementation of a concrete class using the generic DestinationBundle

// Example implementation of a concrete class using the generic DestinationBundle
export class DestinationBundle extends ADestinationBundle {
  allowedEvents: EMableEventName[] = [];
  cookiesToUpdate: string[] = [];
  hasBrowserDestination = false;
  destinationId: string;

  isEventAllowedForDestination(eventName: EMableEventName): boolean {
    return this.allowedEvents.includes(eventName);
  }

  /**
   * Check if the destination is allowed for a given market ID
   * If the destination does not have a list of supported market IDs, then it is allowed for all markets
   * If the destination has an empty list of supported market IDs, then it is allowed for all markets
   * If the destination has a list of supported market IDs, then it is allowed for the given market ID if it is in the list
   * @param marketID - The market ID to check
   * @returns true if the destination is allowed for the market ID, false otherwise
   */
  isMarketAllowedForDestination(marketID: string | undefined): boolean {
    // If market filtering is not enabled, then the destination is allowed for all markets
    if (
      !MARKET_FILTERING_ENABLED ||
      this.destinationConfig.all_markets_selected
    ) {
      return true;
    }
    const supportedMarketIDs = this.destinationConfig.supported_market_ids;
    // If no list of supported market IDs is provided, then the destination is allowed for all markets
    if (!supportedMarketIDs) {
      return true;
    }
    // If array does exist but it is empty, then the destination is allowed for all markets
    if (supportedMarketIDs.length === 0) {
      return true;
    }

    // If the market ID is not provided ie fallback to default market
    if (!marketID) {
      marketID = MarketDataHandler.primaryMarketID;
    }

    // If the destination supports a list of market IDs, then check if the market ID is in the list
    return supportedMarketIDs.includes(marketID);
  }

  // only use when destination has browserDestination
  getBrowserDestinationSendingStatus(): boolean | undefined {
    return undefined;
  }

  isServerDestinationActive(): boolean {
    return this.destinationConfig.active || false;
  }

  updateCookies(
    cookies: Record<string, string>,
    _iqpars: Record<string, string[]>,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _cookieKeysToUpdate: string[] = this.cookiesToUpdate,
  ): Record<string, string> {
    return cookies;
  }

  isBrowserDestinationActive(): boolean {
    return this.destinationConfig.active || false;
  }
  type: EDestinationType;
  destinationConfig: TDestinationCredentials;
  constructor(
    type: EDestinationType,
    destinationConfig: TDestinationCredentials,
    destinationId: string,
    allowedEvents?: EMableEventName[],
  ) {
    super();

    if (allowedEvents?.length === 0) {
      throw new Error('Allowed events cannot be empty');
    }
    this.type = type;
    this.destinationConfig = destinationConfig;
    this.destinationId = destinationId;
  }
  calculatePayload(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _: TMableEvent,
    // shouldSend: boolean,
    // consent: any,
  ): DestinationPayload {
    throw new Error('Method not implemented.');
  }
  async sendToDestination(
    _: DestinationPayload,
  ): Promise<DestinationSendingResult> {
    throw new Error('Method not implemented.');
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async init(_bid: BrowserInstanceData): Promise<void> {
    // Initialization logic specific to MyDestinationBundle
    throw new Error('Method not implemented.');
  }
}
